/**
 * Branded header color tokens. Every derived color comes from two inputs:
 * the header background and the header text color.
 * Formulas follow branded-header-color-rules.md (unified 70/30 boundary).
 */

const LIGHT_L = 70
const DARK_L = 30
const PRESS_ALPHA = 0.42
const PRIMARY_SHIFT = 22
const PRIMARY_L_MIN = 10
const PRIMARY_L_MAX = 96
const SEARCH_OFFSET_LIGHT = -6
const SEARCH_OFFSET_DARK = 12
const CONTRAST_AA = 4.5

const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v))
const lerp = (a, b, t) => a + (b - a) * t
const round2 = (n) => Math.round(n * 100) / 100

/** Parse #RGB or #RRGGBB. Returns null for anything else (rgb(), css vars, empty). */
function parseHex(hex) {
  if (typeof hex !== 'string') return null
  const match = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(hex.trim())
  if (!match) return null
  let digits = match[1]
  if (digits.length === 3)
    digits = digits
      .split('')
      .map((c) => c + c)
      .join('')
  const num = parseInt(digits, 16)
  return { r: (num >> 16) & 255, g: (num >> 8) & 255, b: num & 255 }
}

/** Textbook RGB -> HSL, unrounded. h: 0-360, s/l: 0-100. */
function rgbToHsl({ r, g, b }) {
  const rn = r / 255
  const gn = g / 255
  const bn = b / 255
  const max = Math.max(rn, gn, bn)
  const min = Math.min(rn, gn, bn)
  const l = (max + min) / 2
  let h = 0
  let s = 0
  if (max !== min) {
    const d = max - min
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
    if (max === rn) h = (gn - bn) / d + (gn < bn ? 6 : 0)
    else if (max === gn) h = (bn - rn) / d + 2
    else h = (rn - gn) / d + 4
    h *= 60
  }
  return { h, s: s * 100, l: l * 100 }
}

/** Textbook HSL -> RGB channels 0-255 (unrounded). */
function hslToRgb(h, s, l) {
  const hn = (((h % 360) + 360) % 360) / 360
  const sn = s / 100
  const ln = l / 100
  if (sn === 0) return { r: ln * 255, g: ln * 255, b: ln * 255 }
  const q = ln < 0.5 ? ln * (1 + sn) : ln + sn - ln * sn
  const p = 2 * ln - q
  const hue = (t) => {
    let x = t
    if (x < 0) x += 1
    if (x > 1) x -= 1
    if (x < 1 / 6) return p + (q - p) * 6 * x
    if (x < 1 / 2) return q
    if (x < 2 / 3) return p + (q - p) * (2 / 3 - x) * 6
    return p
  }
  return {
    r: hue(hn + 1 / 3) * 255,
    g: hue(hn) * 255,
    b: hue(hn - 1 / 3) * 255
  }
}

function hslToHex(h, s, l) {
  const { r, g, b } = hslToRgb(h, s, l)
  const toHex = (c) =>
    Math.round(clamp(c, 0, 255))
      .toString(16)
      .padStart(2, '0')
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`.toUpperCase()
}

/** WCAG relative luminance contrast ratio between two RGB objects. */
function contrastRatio(a, b) {
  const lin = (c) => {
    const v = c / 255
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4)
  }
  const relLum = ({ r, g, b }) => 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b)
  const la = relLum(a)
  const lb = relLum(b)
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05)
}

const hsla = (h, s, l, alpha) =>
  `hsla(${round2(h)}, ${round2(s)}%, ${round2(l)}%, ${round2(alpha)})`

/**
 * Compute all branded header tokens from the background and text colors.
 * @param {string} bgHex - header background, #RGB or #RRGGBB
 * @param {string} textHex - header text color, #RGB or #RRGGBB (used as-is)
 * @returns {{
 *   textColor: string, contrastTextBg: number, contrastPasses: boolean,
 *   itemIdle: string, itemHover: string, itemPress: string,
 *   divider: string, label: string, searchSurface: string
 * } | null} null when either color is not a valid hex
 */
export function computeBrandedHeaderTokens(bgHex, textHex) {
  const bgRgb = parseHex(bgHex)
  const textRgb = parseHex(textHex)
  if (!bgRgb || !textRgb) return null

  const bg = rgbToHsl(bgRgb)
  const text = rgbToHsl(textRgb)

  // Shared interpolation: t = 0 at L=70 (light), t = 1 at L=30 (dark), linear in between.
  // Equivalent to the regime-based constants in md sections 2 and 4 at both ends.
  const t = clamp((LIGHT_L - bg.l) / (LIGHT_L - DARK_L), 0, 1)

  const idleAlpha = lerp(0.04, 0.12, t)
  const hoverAlpha = lerp(0.12, 0.22, t)
  const dividerAlpha = lerp(0.14, 0.24, t)

  // md section 3 (aligned to the 70 boundary): push text L away from the background
  const primaryL =
    bg.l >= LIGHT_L
      ? clamp(text.l - PRIMARY_SHIFT, PRIMARY_L_MIN, PRIMARY_L_MAX)
      : clamp(text.l + PRIMARY_SHIFT, PRIMARY_L_MIN, PRIMARY_L_MAX)

  // md section 5 (unified fix): offset lerps from -6 (light) to +12 (dark) on the same t
  const searchOffset = lerp(SEARCH_OFFSET_LIGHT, SEARCH_OFFSET_DARK, t)
  const searchL = clamp(bg.l + searchOffset, 0, 100)

  const contrast = contrastRatio(textRgb, bgRgb)

  return {
    textColor: textHex,
    contrastTextBg: round2(contrast),
    contrastPasses: contrast >= CONTRAST_AA,
    itemIdle: hsla(text.h, text.s, text.l, idleAlpha),
    itemHover: hsla(text.h, text.s, text.l, hoverAlpha),
    itemPress: hsla(text.h, text.s, text.l, PRESS_ALPHA),
    divider: hsla(text.h, text.s, text.l, dividerAlpha),
    label: hslToHex(text.h, text.s, primaryL),
    searchSurface: hslToHex(bg.h, bg.s, searchL)
  }
}
